import React, { useLayoutEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

const SLIDE_MS = 240;

type SlideDirection = 'left' | 'right';

const slideFromRight = keyframes({
  from: { transform: 'translateX(100%)' },
  to: { transform: 'translateX(0)' },
});

const slideFromLeft = keyframes({
  from: { transform: 'translateX(-100%)' },
  to: { transform: 'translateX(0)' },
});

const useStyles = makeStyles()({
  frame: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
  },
  frameFill: {
    overflowY: 'hidden',
  },
  slide: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minWidth: 0,
  },
  slideFill: {
    flex: '1 1 0',
    minHeight: 0,
  },
  right: {
    animation: `${slideFromRight} ${SLIDE_MS}ms ease-out both`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  left: {
    animation: `${slideFromLeft} ${SLIDE_MS}ms ease-out both`,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
});

/** Which way `to` sits in `order` relative to `from`. */
export const panelTabSlideDirection = (
  order: readonly string[],
  from: string | undefined,
  to: string,
): SlideDirection | undefined => {
  if (!from || from === to) return;
  const fromIndex = order.indexOf(from);
  const toIndex = order.indexOf(to);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
  return toIndex > fromIndex ? 'right' : 'left';
};

/**
 * Direction of a tab change, kept until the next change.
 * The previous tab is stored after commit so a render restarted by setState still sees it.
 */
export const usePanelTabSlideDirection = (
  order: readonly string[],
  tab: string,
): SlideDirection | undefined => {
  const committed = useRef<string | undefined>(undefined);
  const direction = useRef<SlideDirection | undefined>(undefined);
  if (committed.current !== tab) {
    direction.current = panelTabSlideDirection(order, committed.current, tab);
  }
  useLayoutEffect(() => {
    committed.current = tab;
  }, [tab]);
  return direction.current;
};

type PanelTabSlideProps = {
  /** Tab identity. Changing it replays the enter animation. */
  slideKey: string;
  direction?: SlideDirection;
  /** Fill the panel and scroll inside the tab, instead of growing the frame. */
  fill?: boolean;
  className?: string;
  children: React.ReactNode;
};

export const PanelTabSlide: React.FC<PanelTabSlideProps> = ({
  slideKey,
  direction,
  fill = false,
  className,
  children,
}) => {
  const { classes, cx } = useStyles();
  return (
    <Box className={cx(classes.frame, fill && classes.frameFill, className)}>
      <Box
        key={slideKey}
        className={cx(
          classes.slide,
          fill && classes.slideFill,
          direction === 'right' && classes.right,
          direction === 'left' && classes.left,
        )}
      >
        {children}
      </Box>
    </Box>
  );
};
